import { resolveSemanticColor } from '@/theme/semanticTokens';
import {
  elevationTokens,
  type ElevationToken,
  type RadiusToken,
  type SpacingToken,
} from '@/theme/tokens';
import { r, s } from '@/theme/tokens';
import { Box } from './Box';
import { useTheme } from './hooks';
import type { SxProps, Theme } from './types';
import type { ReactElement, ReactNode } from 'react';

const SPACING_MAP: Record<SpacingToken, string> = {
  none: s('none'),
  xxxs: s('xxxs'),
  xxs: s('xxs'),
  xs: s('xs'),
  sm: s('sm'),
  md: s('md'),
  lg: s('lg'),
  xl: s('xl'),
  '2xl': s('2xl'),
  '3xl': s('3xl'),
  '4xl': s('4xl'),
  '5xl': s('5xl'),
  gutter: s('gutter'),
  section: s('section'),
};

const RADIUS_MAP: Record<RadiusToken, string> = {
  none: r('none'),
  sm: r('sm'),
  md: r('md'),
  card: r('card'),
};

const resolveSpacing = (token: SpacingToken | null | undefined): string | undefined =>
  token ? SPACING_MAP[token] : undefined;

type SurfaceProps = {
  children: ReactNode;
  padding?: SpacingToken | null;
  radius?: RadiusToken;
  elevation?: ElevationToken;
  border?: boolean;
  background?: 'surface' | 'transparent';
  sx?: SxProps<Theme>;
};

export const Surface = ({
  children,
  padding = 'md',
  radius = 'card',
  elevation = 'none',
  border = true,
  background = 'surface',
  sx,
}: SurfaceProps): ReactElement => {
  const theme = useTheme();
  const baseSx: SxProps<Theme> = {
    backgroundColor:
      background === 'transparent'
        ? 'transparent'
        : `${resolveSemanticColor(theme, 'surface')} !important`,
    backgroundImage: 'none',
    borderRadius: RADIUS_MAP[radius],
    boxShadow: elevationTokens[elevation],
    border: border ? `1px solid ${resolveSemanticColor(theme, 'border')}` : 'none',
    boxSizing: 'border-box',
    opacity: 1,
    isolation: 'isolate',
    minWidth: 0,
    p: resolveSpacing(padding),
  };
  const resolvedSx = sx ? [baseSx, ...(Array.isArray(sx) ? sx : [sx])] : baseSx;

  return <Box sx={resolvedSx}>{children}</Box>;
};
